export const exercisesContent = /* html */ `

<div data-id="exercises-conditionals" class="level1-container">
  <h1 class="header">Conditionals</h1>

  <div data-id="ex-conditionals-ternary operator">
    <h2>Ternary operator</h2>
    <div class="info">
      <p><b>Firefly signals</b></p>
      <p>Fireflies only flash on warm evenings.</p>
      <ul>
        <li>Store an evening temperature in a variable.</li>
        <li>Use a single ternary to store either "Flashing" or "Resting" in a second variable, using 18 degrees as the threshold.</li>
        <li>Log the result inside a template literal that also mentions the temperature.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-conditionals-switch">
    <h2>Switch</h2>
    <div class="info">
      <p><b>Frog chorus</b></p>
      <p>Write a switch on a species variable that logs a different call for a tree frog, a bullfrog and a spring peeper.</p>
      <ul>
        <li>Add a default for unknown species.</li>
        <li>Let two species share the same call without repeating the code.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-conditionals-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Hedgehog hibernation report</b></p>
      <p>A rescue centre decides per hedgehog what happens before winter. One hedgehog has a name, a weight in grams, a season and a health score from 1 to 5.</p>
      <ul>
        <li>Store one hedgehog in a set of variables, and write the whole decision below it, so you can rerun it by only changing those values.</li>
        <li>Use a switch on the season that groups autumn and winter together as "hibernation possible", spring and summer as "active", and has a default for anything unexpected.</li>
        <li>Let each branch of the switch store its outcome in one variable, instead of logging from inside the switch.</li>
        <li>In the hibernation branch, let a nested ternary decide on the weight: under 450 grams is "too light", 450 to 599 is "borderline", 600 or more is "ready".</li>
        <li>Build the final report line with a template literal, and use a ternary inside one of its placeholders to say whether the hedgehog stays at the centre or is released, based on the health score.</li>
        <li>Rerun the whole thing for at least four hedgehogs that together reach every branch you wrote, including the default.</li>
        <li>Challenge: solve the whole exercise without writing a single if statement.</li>
      </ul>
    </div>
  </div>
</div>

<!-- STRINGS -->

<div data-id="exercises-strings" class="level1-container">
  <h1>Strings</h1>

  <div data-id="ex-strings-template literals">
    <h2>Template literals</h2>
    <div class="info">
      <p><b>Owl nest report</b></p>
      <p>Store a species, a number of chicks and a tree name in variables.</p>
      <ul>
        <li>Build one report sentence with a template literal that uses all three.</li>
        <li>Include a calculation inside the string (let's assume half of the chicks have fledged).</li>
        <li>Make the report span two lines without using a single escape character (so no /n etc).</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-strings-string operations">
    <h2>String operations</h2>
    <div class="info">
      <p><b>Moss sample labels</b></p>
      <p>Start from a messy label such as "   MOSS-fern_valley-2024  ".</p>
      <ul>
        <li>Clean it up and split it into its three parts.</li>
        <li>Turn the location part into "Fern valley" (lowercase, underscore replaced, first letter capitalised).</li>
        <li>Take the prefix part (the first of the three, e.g. "MOSS") and check, case-insensitively, whether it equals "moss". Log a message stating whether the label is a moss sample.</li>
        <li>Log three separate checks: whether the label contains "fern", whether it starts with "MOSS" and whether it ends with "2024".</li>
        <li>Log the three parts as an aligned table by padding each one to a fixed width, and close it off with a separator line of twenty dashes that you do not type out by hand.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-strings-string conversion">
    <h2>String conversion</h2>
    <div class="info">
      <p><b>Toad weigh-in</b></p>
      <p>You track a toad's weight in a number variable, e.g. 42 (grams).</p>
      <ul>
        <li>Log a sentence built with string concatenation that includes the weight, without calling any conversion function and no template literal.</li>
        <li>Convert the weight to a string using two different ways, and log the typeof of each result.</li>
        <li>Now assume the weight is missing (null). Show what happens when you call .toString() on it, then convert it safely in another way instead.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-strings-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Bird ringing station</b></p>
      <p>A ringing station hands you a messy batch of codes, for example "  nl-2021-a0473 ", "BE-2019-X8812", the broken entry "DE-2020" and one entry that is null.</p>
      <ul>
        <li>Before you touch a code, turn it into something safe to work with, so the null entry never reaches your string methods and throws.</li>
        <li>Clean every remaining code up: trim it, put country and serial in capitals, and split it into country, year and serial.</li>
        <li>Detect codes that do not have exactly three parts, log a warning for those, and keep them out of the rest of the run.</li>
        <li>Rebuild every valid code as "A0473 (NL, ringed 2021)".</li>
        <li>Convert the year to a number in two different ways, and log the typeof of both results to prove they really are numbers.</li>
        <li>Compute the age of the bird in years from that number, and mention it in the report line.</li>
        <li>Print the valid codes as an aligned table by padding country, year and serial to fixed widths, closed off with a separator line of dashes you do not type out by hand.</li>
        <li>Challenge: end with a count of how many codes were valid, skipped and empty, and prove your cleaning step handled all three cases without a single crash.</li>
      </ul>
    </div>
  </div>
</div>

<!-- LOOPS -->

<div data-id="exercises-loops" class="level1-container">
  <h1>Loops</h1>

  <div data-id="ex-loops-for">
    <h2>For</h2>
    <div class="info">
      <p><b>Pinecone count</b></p>
      <p>A forester counts pinecones under ten trees.</p>
      <ul>
        <li>Loop over an array of counts with a classic for loop and log a line per tree.</li>
        <li>Skip trees where the count is zero.</li>
        <li>Stop the loop entirely once the running total passes 100, and log that total.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-loops-for of">
    <h2>For of</h2>
    <div class="info">
      <p><b>Butterfly transect</b></p>
      <p>Walk an array of butterfly species with a for of loop.</p>
      <ul>
        <li>Log each species on its own line.</li>
        <li>Then log them numbered from 1, without a separate counter variable.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-loops-for each">
    <h2>For each</h2>
    <div class="info">
      <p><b>Beetle traps</b></p>
      <p>You have an array of beetle counts per trap.</p>
      <ul>
        <li>Use forEach to log "Trap 3: 12 beetles" style lines.</li>
        <li>Try to stop the loop at the first empty trap and log what you observe.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-loops-for in">
    <h2>For in</h2>
    <div class="info">
      <p><b>Badger sett</b></p>
      <p>Describe a badger sett as an object with entrances, depth and occupants.</p>
      <ul>
        <li>Loop over it and log each property as "key: value". Log 'key: key+1'.<br />
        Look closely at that second result, is it doing what you would expect?<br />
        Check what the type of the key is, and deduce why you should not use for..in on arrays</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-loops-while">
    <h2>While</h2>
    <div class="info">
      <p><b>Frog pond</b></p>
      <p>A pond starts with 3 frogs and the population doubles every year.</p>
      <ul>
        <li>Use a while loop to log the population per year until it passes 100, then log how many years that took.</li>
        <li>Use a do...while loop to draw random numbers between 1 and 10 until you draw a 7, then log how many draws you needed.</li>
        <li>Start the first loop with 200 frogs instead. Compare what a while and a do...while log in that case.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-loops-iterables">
    <h2>Iterables</h2>
    <div class="info">
      <p><b>Owl pellets</b></p>
      <p>You have an array of prey species found in owl pellets, and an object mapping each species to how often it was found.</p>
      <ul>
        <li>For both the array and the object, log whether [Symbol.iterator] is a function.</li>
        <li>Get the iterator from the array and call next() by hand until done is true, logging each result.</li>
        <li>Run a for of loop over the object, read the error, then make it work with Object.entries().</li>
        <li>Log the length of Object.entries(obj), then try the same on arr.entries() and log what you observe.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-loops-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Woodland survey</b></p>
      <p>Build an array of survey plots. Every plot is an object with a name, an array of tree species and an object with soil readings (ph, moisture, depth).</p>
      <ul>
        <li>Use a classic for loop to walk the plots in order, logging each one with its position, skipping the empty ones, and stopping the survey completely once the running total reaches 100 trees.</li>
        <li>Use a for of loop with entries() to log the species of one plot, numbered from 1, without a separate counter variable.</li>
        <li>Use forEach to log a one line summary per plot, then note in a comment why you cannot break out of it.</li>
        <li>Use for in on the soil readings of one plot, and explain in a comment why that same loop is a bad idea on the species array.</li>
        <li>Use a while loop to keep visiting plots until the running total of trees passes a threshold, and log how many plots that took.</li>
        <li>Make a plot object walkable by a for of loop, and log its key value pairs.</li>
        <li>Challenge: log the plot with the most species and the average number of species per plot, using only loops and no array methods.</li>
      </ul>
    </div>
  </div>
</div>

<!-- FUNCTIONS -->

<div data-id="exercises-functions" class="level1-container">
  <h1>Functions</h1>

  <div data-id="ex-functions-arrow functions">
    <h2>Arrow functions</h2>
    <div class="info">
      <p><b>Dragonfly wingbeat</b></p>
      <ul>
        <li>Write a one-line arrow function that converts wingbeats per second into wingbeats per minute.</li>
        <li>Write a second arrow function that takes a species and a wingbeat rate and returns an object with both, without using the return keyword.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-functions-function declaration">
    <h2>Function declaration</h2>
    <div class="info">
      <p><b>Tree rings</b></p>
      <ul>
        <li>Declare a function that turns a ring count into an age and a planting year.</li>
        <li>Call it on the line above its own definition and explain in a log why that works.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-functions-function expression">
    <h2>Function expression</h2>
    <div class="info">
      <p><b>Tide height</b></p>
      <ul>
        <li>Store a function in a variable that returns the difference between high and low tide.</li>
        <li>Call it before the line where it is defined and log the error you get.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-functions-default function values">
    <h2>Default function values</h2>
    <div class="info">
      <p><b>Seed packet</b></p>
      <ul>
        <li>Write a function print(species, amount, label)</li>
        <li>All parameters should have default values. But the label parameter default has to be built from the other two parameters</li>
        <li>Call it once with no arguments and once with only the species.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-functions-callback and higher order functions">
    <h2>Callback and higher order functions</h2>
    <div class="info">
      <p><b>Leaf sorter</b></p>
      <ul>
        <li>Write a higher order function that takes an array of leaf lengths and a callback, and logs the result of applying that callback to each leaf.</li>
        <li>Call it with two different callbacks: one that converts to inches, one that labels each leaf small or large.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-functions-immediately invoked function expressions">
    <h2>Immediately invoked function expressions</h2>
    <div class="info">
      <p><b>Glacier measurement</b></p>
      <ul>
        <li>Write an IIFE that computes this year's glacier retreat once and logs it.</li>
        <li>Prove from the outside that the function is not reachable</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-functions-closures">
    <h2>Closures</h2>
    <div class="info">
      <p><b>Squirrel nut counter</b></p>
      <ul>
        <li>Write a function that creates a nut counter for one squirrel and returns a function that adds nuts and logs the new total.</li>
        <li>Create two squirrels and prove that their totals are independent.</li>
        <li>Declare one empty variable outside two other functions, and let each of those functions assign a new function to it that logs one of its own local variables. Call them in turn and explain why the same variable logs something different each time.</li>
      </ul>
      <p><b>Reserve population log</b></p>
      <ul>
        <li>A nature reserve keeps counts of its animal populations. Every change to a count can be undone, but only the most recent change.</li>
        <li>Declare an object populations holding a few species (key) and their counts (value), and next to it one empty variable undoLastChange.</li>
        <li>Write recordBirths(species, amount): raises that species' count, logs the new count, and assigns a new function to undoLastChange that restores the count to what it was before this call.</li>
        <li>Write recordDeaths(species, amount): lowers that species' count, logs the new count, and assigns a new function to undoLastChange that restores the count to what it was before this call.</li>
        <li>Call recordBirths, then undoLastChange, then recordDeaths, then undoLastChange, logging the counts along the way, and explain why undoLastChange restores a different species and a different value each time, even though you call the exact same variable.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-functions-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Beaver dam workshop</b></p>
      <p>Three beavers each build their own dam on the same stream. They disagree about how much a branch is worth, so the way a dam measures a branch is handed to it as a function, its "strategy". A strategy is nothing more than a function that takes a branch (and, if it wants to, the dam's current height) and returns a number: how much height that branch adds.</p>
      <ul>
        <li>Declare a streamWidth constant above buildDam: all three beavers share the same stream, so this value exists only once.</li>
        <li>Write buildDam as a function expression that takes a beaver name and a strategy function. It keeps the current height (a number, starting at 0) and a branch count (a number, starting at 0) private in a closure, and returns an object with three functions: addBranch, report and reset.</li>
        <li>Give buildDam a default strategy, so a beaver that does not hand one in still builds a dam, and a maxHeight parameter that defaults to streamWidth * 0.5 (a wider stream can carry a taller dam), so a beaver only has to pass one when it wants to override that.</li>
        <li>addBranch takes a branch (an object with a length and a thickness, both in cm), calls the strategy to find out how much height (in metres) it adds, adds that to the dam's height, increases the branch count by one, and refuses the branch once the maximum height is reached.</li>
        <li>Give each beaver a strategy that genuinely needs different information, which is what decides the arrow notation of each one: the lazy beaver ignores the branch entirely and always adds a flat 0.05, the careful beaver only looks at the branch and adds (length * thickness) / 1000, and the cautious beaver uses that same formula but also divides it by (1 + the current height), so it adds less as the dam grows.</li>
        <li>report returns a summary of that one dam: the beaver's name, the current height and the branch count. Write the line that actually prints such a summary as a separate function declaration, call it from every report, and put it at the bottom of your file to show that you can use it before it is defined.</li>
        <li>reset sets that dam's private height and branch count back to 0, without calling buildDam again.</li>
        <li>Let the three beavers add several branches, report the dams, and prove that resetting one dam leaves the other two untouched.</li>
        <li>Wrap the whole simulation in an IIFE, so the stream settings stay private and the script runs once.</li>
        <li>Challenge: let addBranch schedule an inspection with setTimeout that logs the height of that dam two seconds later, and explain in a comment why every inspection reports its own dam and not the last one built.</li>
      </ul>
    </div>
  </div>
</div>

<!-- DATA STRUCTURES -->

<div data-id="exercises-data structures" class="level1-container">
  <h1>Data structures</h1>

  <div data-id="ex-data structures-overview">
    <h2>Overview</h2>
    <div class="info">
      <p><b>Choosing a container</b></p>
      <p>Build the most suitable structure for each of these, and log why in one sentence each.</p>
      <ul>
        <li>The ordered list of species seen during one walk.</li>
        <li>The unique species seen during that walk.</li>
        <li>The profile of a single nature reserve (name, area, founded).</li>
        <li>The number of sightings per species, where species names come from user input.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-data structures-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Rockpool inventory</b></p>
      <p>A survey visits three rockpools. Every pool is an object with a location, a depth and a raw list of sightings that contains plenty of duplicates, for example ["crab", "limpet", "crab", "anemone", "limpet", "crab"].</p>
      <ul>
        <li>Store the three rockpools in the structure that fits an ordered series of the same kind of thing, and the details of one pool in the structure that fits named properties.</li>
        <li>Per pool, derive the species that were actually seen there with the structure that is built for uniqueness, and log how many unique species each pool holds.</li>
        <li>Across all three pools, build one structure that holds the total count per species, choosing the one that keeps insertion order and accepts any key type.</li>
        <li>Convert that count structure into a plain object so it can be logged as a table, then rebuild the original structure from it and prove nothing was lost.</li>
        <li>For each of the four structures you used, write a comment stating why it is the right choice there and what an alternative would cost you.</li>
        <li>Challenge: log which species were seen in every single pool, and which in only one.</li>
      </ul>
    </div>
  </div>
</div>

<!-- ARRAYS -->

<div data-id="exercises-arrays" class="level1-container">
  <h1>Arrays</h1>

  <div data-id="ex-arrays-destructuring">
    <h2>Destructuring</h2>
    <div class="info">
      <p><b>Fox den coordinates</b></p>
      <ul>
        <li>Destructure latitude and longitude out of a coordinate array.</li>
        <li>Swap them in a single line, without a helper variable.</li>
        <li>From an array holding a den name and a nested array of cub weights, pull out the name, the first cub weight and the rest of the weights in one statement.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-non-mutating methods">
    <h2>Non-mutating methods</h2>
    <div class="info">
      <p><b>Crane migration</b></p>
      <p>Start from an array of flock sizes.</p>
      <ul>
        <li>Create a new array with every flock size doubled.</li>
        <li>Create another with only the flocks larger than 50.</li>
        <li>Take the first three flocks into a separate array, then prove the original array never changed.</li>
        <li>Join a second season's flock sizes onto the first into one new array, without touching either original.</li>
        <li>Log every flock size on one line, first with a separator you pick yourself and then by letting the array turn itself into a string.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-mutating methods">
    <h2>Mutating methods</h2>
    <div class="info">
      <p><b>Ant trail</b></p>
      <p>Start from an array of ants on a trail.</p>
      <ul>
        <li>Add one ant at the end and one at the front.</li>
        <li>Remove the ant at the front and log which one left.</li>
        <li>Replace the two ants in the middle with three new ones, and reverse the trail.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-searching and indexing">
    <h2>Searching and indexing</h2>
    <div class="info">
      <p><b>Mushroom basket</b></p>
      <p>Use an array of mushroom objects with a name and an edible flag.</p>
      <ul>
        <li>Find the first edible mushroom and its position in the basket.</li>
        <li>Check whether the basket contains any poisonous one, and whether they are all identified.</li>
        <li>Log whether the exact name "morel" is present.</li>
        <li>From a separate array of picked names that contains duplicates, log the first and the last position where "chanterelle" occurs.</li>
        <li>Log the last mushroom in the basket in three different ways.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-aggregation methods">
    <h2>Aggregation methods</h2>
    <div class="info">
      <p><b>Honey harvest</b></p>
      <p>Use an array of hive objects with a hive name and a harvest in kilos.</p>
      <ul>
        <li>Compute the total harvest with reduce.</li>
        <li>Find the best hive with reduce, not with sort.</li>
        <li>Build an object that holds the harvest per hive, again with reduce.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-aggregation methods challenge">
    <h2>Aggregation methods challenge</h2>
    <div class="info">
      <p><b>Woodland survey, revisited</b></p>
      <p>Take the "Woodland survey" chapter challenge from the Loops chapter (an array of plot objects, each with a plot name and an array of tree species) and solve it again, this time without writing a single loop keyword.</p>
      <ul>
        <li>Use reduce (or flatMap) to build one flat array of every species across all plots.</li>
        <li>Use reduce to count the total number of trees recorded.</li>
        <li>Use reduce to find the plot with the most species, and log its name.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-flattening">
    <h2>Flattening</h2>
    <div class="info">
      <p><b>Forest layers</b></p>
      <p>Model a forest as an array of layers, each layer an array of species, where one layer itself contains sub-arrays.</p>
      <ul>
        <li>Produce one flat list of all species.</li>
        <li>Produce a flat list of species names in capitals, in a single method call.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-sorting">
    <h2>Sorting</h2>
    <div class="info">
      <p><b>Whale sightings</b></p>
      <ul>
        <li>Sort an array of sighting distances from near to far, and then from far to near.</li>
        <li>Sort an array of sighting objects by date, newest first.</li>
        <li>Sort species names alphabetically without changing the original array.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-advanced array creation">
    <h2>Advanced array creation</h2>
    <div class="info">
      <p><b>Coral reef grid</b></p>
      <ul>
        <li>Create an array of twelve monitoring slots, all filled with zero.</li>
        <li>Create an array holding the numbers 1 to 12 in one statement.</li>
        <li>Turn the string "CORAL" into an array of its letters, and turn a set of reef names back into an array.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-arrays-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Bird survey pipeline</b></p>
      <p>Start from an array of observation objects, each with a species, a count, a habitat and an array of observer names.</p>
      <ul>
        <li>Destructure the first observation into separate variables and collect the remaining ones with a rest element.</li>
        <li>Generate ten extra dummy observations in a single statement, without typing them out by hand.</li>
        <li>In one chain, keep only the woodland observations, reduce them to a total count per species, and log the top three species from high to low.</li>
        <li>Pull all observer names out of every observation into one flat list with a single method call, then remove the duplicates from that list in a second step.</li>
        <li>Find the first observation with a count above ten, and log both the observation itself and its index.</li>
        <li>Add a new observation to a copy of the list with a non-mutating method, add another one to the original with a mutating method, and log the difference between the two.</li>
        <li>Sort the observations alphabetically by species without touching the original array, and prove the original is unchanged.</li>
        <li>Challenge: mark in a comment, for every method you used, whether it mutates or returns something new, and fix any spot where you relied on the original still being intact.</li>
      </ul>
    </div>
  </div>
</div>

<!-- OBJECTS -->

<div data-id="exercises-objects" class="level1-container">
  <h1>Objects</h1>

  <div data-id="ex-objects-accessing properties">
    <h2>Accessing properties</h2>
    <div class="info">
      <p><b>Otter profile</b></p>
      <p>Build an otter object with a name, a weight and a nested object holding river and country.</p>
      <ul>
        <li>Log the country using dot notation and the weight using a property name stored in a variable.</li>
        <li>Add a new property after creation and delete an existing one.</li>
        <li>Log what you get when you read a property that does not exist.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-objects-destructuring">
    <h2>Destructuring</h2>
    <div class="info">
      <p><b>Storm report</b></p>
      <p>Use a report object with wind, rain and a nested location object.</p>
      <ul>
        <li>Destructure wind and rain, renaming rain to rainfall.</li>
        <li>Destructure the nested city, with a default for a missing one.</li>
        <li>Write a function that takes the whole report but destructures it in its parameter list.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-objects-looping over objects">
    <h2>Looping over objects</h2>
    <div class="info">
      <p><b>Soil samples</b></p>
      <p>Use an object with a pH, a nitrogen level and a moisture level.</p>
      <ul>
        <li>Log only the property names, then only the values.</li>
        <li>Log "key: value" lines using entries and destructuring.</li>
        <li>Log the average of all values.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-objects-cloning and freezing">
    <h2>Cloning and freezing</h2>
    <div class="info">
      <p><b>Nature reserve rules</b></p>
      <p>Use a reserve object with a nested rules object.</p>
      <ul>
        <li>Make a shallow copy, change a nested rule, and show that both objects changed.</li>
        <li>Make a deep copy and show that this time they are independent.</li>
        <li>Freeze the original and show what happens when you try to change it.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-objects-enhanced object literals">
    <h2>Enhanced object literals</h2>
    <div class="info">
      <p><b>Wolf pack</b></p>
      <ul>
        <li>Build a pack object from existing variables using shorthand properties.</li>
        <li>Add a howl method using the short method syntax.</li>
        <li>Add a property whose key comes from a variable, for example the current season.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-objects-getters and setters">
    <h2>Getters and setters</h2>
    <div class="info">
      <p><b>Aquarium tank</b></p>
      <ul>
        <li>Build a tank object with length, width and height, and a getter that returns the volume.</li>
        <li>Add a setter for the temperature that refuses values outside 18 to 28 degrees and logs a warning instead.</li>
        <li>Read and write both as if they were plain properties.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-objects-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Wildlife camera</b></p>
      <p>Build a camera object that stores captures and can report on them.</p>
      <ul>
        <li>Build the camera with a location, an array of captures and a method that adds a capture, written with the short method syntax.</li>
        <li>Use a variable as a computed key, so the camera stores its settings under a name that is only known at runtime.</li>
        <li>Add a getter for the most recent capture and one for the number of captures, plus a setter that renames the location but refuses an empty string.</li>
        <li>Destructure the location and the capture count in one statement, renaming one of them and giving the other a default value.</li>
        <li>Loop over the captures and log a summary, then loop over the camera itself and log only the keys, only the values, and finally both together.</li>
        <li>Archive the camera twice, once as a shallow copy and once as a deep copy. Then add a capture to the original and log both archives to show which one changed with it.</li>
        <li>Freeze the shallow archive, try to rename its location, and then add a capture to its nested array anyway. Explain in a comment why the second one still works.</li>
        <li>Challenge: make an archive that really is protected all the way down, and prove it by trying to change both its location and its captures.</li>
      </ul>
    </div>
  </div>
</div>

<!-- MAPS -->

<div data-id="exercises-maps" class="level1-container">
  <h1>Maps</h1>

  <div data-id="ex-maps-usage">
    <h2>Usage</h2>
    <div class="info">
      <p><b>Bat roost</b></p>
      <ul>
        <li>Create a map that holds the number of bats per roost, adding entries in a single chain.</li>
        <li>Read one roost, check whether another exists, delete a third and log the remaining size.</li>
        <li>Add an entry whose key is an array, and show why reading it back with an identical literal fails.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-maps-looping over maps">
    <h2>Looping over maps</h2>
    <div class="info">
      <p><b>Tide table</b></p>
      <p>Use a map with times as keys and water heights as values.</p>
      <ul>
        <li>Log every entry as "time: height" using destructuring.</li>
        <li>Log only the times, then only the heights.</li>
        <li>Log the highest water level in the table.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-maps-converting">
    <h2>Converting</h2>
    <div class="info">
      <p><b>Pollen counts</b></p>
      <ul>
        <li>Turn an object with pollen counts per tree species into a map in one statement.</li>
        <li>Turn that map into an array of entries, and sort it by count.</li>
        <li>Turn the sorted result back into a plain object.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-maps-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Ranger logbook</b></p>
      <p>Several rangers report sightings during a season.</p>
      <ul>
        <li>Build a map where each key is a ranger object (not a string) and each value an array of the sightings of that ranger.</li>
        <li>Write a function that adds a sighting and creates the array when the ranger is new, chaining the set calls where you can.</li>
        <li>Check whether a ranger already exists before adding, and log the size of the map after every insertion.</li>
        <li>Loop over the map to log how many sightings each ranger made, sorted from most to least, destructuring key and value in the loop head.</li>
        <li>Convert the map into an array of pairs, and rebuild a map from that array again.</li>
        <li>Try to convert the map straight into a plain object, log the result, and explain in a comment why all rangers end up under one key. Then build the object you actually wanted by using the ranger name as the key.</li>
        <li>Challenge: explain in a comment why looking up a ranger by object key only works when you keep a reference to that exact object, and show it failing with a lookalike.</li>
      </ul>
    </div>
  </div>
</div>

<!-- SETS -->

<div data-id="exercises-sets" class="level1-container">
  <h1>Sets</h1>

  <div data-id="ex-sets-usage">
    <h2>Usage</h2>
    <div class="info">
      <p><b>Rockpool species</b></p>
      <ul>
        <li>Turn an array of species with duplicates into a set and log how many unique species there are.</li>
        <li>Add a new species, try to add an existing one again, and check the size after each step.</li>
        <li>Check whether a given species is present, then remove it.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-sets-looping over sets">
    <h2>Looping over sets</h2>
    <div class="info">
      <p><b>Dawn chorus</b></p>
      <ul>
        <li>Loop over a set of singing birds and log each one numbered.</li>
        <li>Log the alphabetically first species in the set.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-sets-converting">
    <h2>Converting</h2>
    <div class="info">
      <p><b>Transect comparison</b></p>
      <p>You have two arrays of species, one per transect.</p>
      <ul>
        <li>Produce a deduplicated array of everything seen on both transects together.</li>
        <li>Produce an array of the species seen on both.</li>
        <li>Produce an array of the species seen only on the first.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-sets-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Species checklist</b></p>
      <p>You have a checklist of target species and a long array of actual sightings with plenty of duplicates.</p>
      <ul>
        <li>Turn both lists into sets and log how many unique species each one holds.</li>
        <li>Log which targets were found (the intersection) and which are still missing (the difference).</li>
        <li>Log every species that was seen but is not on the checklist at all.</li>
        <li>Build the union of both lists, and remove the duplicates from the raw sightings array in a single line.</li>
        <li>Loop over one of the sets and log its entries, then explain in a comment why key and value are identical there.</li>
        <li>Report all results as sorted arrays, not as sets.</li>
        <li>Challenge: add two different objects that look exactly the same to a set, log the size, and explain the result.</li>
      </ul>
    </div>
  </div>
</div>

<!-- OPERATORS -->

<div data-id="exercises-operators" class="level1-container">
  <h1>Operators</h1>

  <div data-id="ex-operators-spread and rest operator">
    <h2>Spread and rest operator</h2>
    <div class="info">
      <p><b>Seed mix</b></p>
      <ul>
        <li>Merge two arrays of seed types into one new array, with an extra seed in the middle.</li>
        <li>Merge a base mix object with an override object so the override wins.</li>
        <li>Write a function that accepts any number of seed names and logs the first one plus the count of the rest.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-operators-short circuiting">
    <h2>Short circuiting</h2>
    <div class="info">
      <p><b>Weather station fallback</b></p>
      <ul>
        <li>Log a station name, falling back to "Unknown station" when it is missing.</li>
        <li>Log a measured rainfall of 0 with a fallback, first in a way that wrongly replaces the zero, then in a way that keeps it.</li>
        <li>Use an and-operator to call a logging function only when the station is online, without an if.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-operators-logical assignment operators">
    <h2>Logical assignment operators</h2>
    <div class="info">
      <p><b>Nest box defaults</b></p>
      <p>Use an array of nest box objects where some properties are missing or zero.</p>
      <ul>
        <li>Give every box a default owner, without overwriting existing owners.</li>
        <li>Give every box a default entrance size, keeping a size of 0 intact.</li>
        <li>Only update the note property on boxes that already have one.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-operators-optional chaining">
    <h2>Optional chaining</h2>
    <div class="info">
      <p><b>Sighting record</b></p>
      <p>Use a sighting object where the observer, their contact details and a photo method may or may not exist.</p>
      <ul>
        <li>Read a deeply nested email without throwing when a level is missing.</li>
        <li>Call the photo method only if it exists.</li>
        <li>Read a property whose key is in a variable, and fall back to "unknown" when the result is null or undefined.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-operators-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Trail camera config</b></p>
      <p>A trail camera has a default config, and users hand in partial configs that may contain zeros, empty strings and missing nested settings.</p>
      <ul>
        <li>Write a function that merges a partial config onto the defaults with the spread operator, without mutating either one.</li>
        <li>Give that function a rest parameter so it accepts several partial configs at once and merges them all in order.</li>
        <li>Fill in missing values with the operator that only replaces null and undefined, so that 0 and "" survive.</li>
        <li>Use short circuiting to log a warning only when a camera has no name, without writing an if statement.</li>
        <li>Use logical assignment operators to set a default resolution, and to bump a counter only when it already exists.</li>
        <li>Read an optional nested night mode setting safely, including a method on it that may not exist, and log the final config.</li>
        <li>Challenge: log two examples that show the exact difference between the OR operator and the nullish coalescing operator in this config.</li>
      </ul>
    </div>
  </div>
</div>

<!-- JAVASCRIPT UTILITIES -->

<div data-id="exercises-javascript utilities" class="level1-container">
  <h1>Javascript utilities</h1>

  <div data-id="ex-javascript utilities-numbers">
    <h2>Numbers</h2>
    <div class="info">
      <p><b>Snowfall</b></p>
      <ul>
        <li>Convert the strings "23.7cm" and "8cm" to numbers and add them.</li>
        <li>Log the total rounded to one decimal, and check whether the raw total is a whole number.</li>
        <li>Log the result of 0.1 + 0.2 and then log a version that reads as expected.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-javascript utilities-dates">
    <h2>Dates</h2>
    <div class="info">
      <p><b>Salmon run</b></p>
      <ul>
        <li>Create a date for the start of the run and one for today.</li>
        <li>Log how many whole days lie between them.</li>
        <li>Log the date two weeks after the start, as day, month and year.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-javascript utilities-internationalization">
    <h2>Internationalization</h2>
    <div class="info">
      <p><b>Global reserve report</b></p>
      <ul>
        <li>Format a reserve area of 12480.5 hectares for a Belgian and for a US reader.</li>
        <li>Format the same number as a currency amount of donations.</li>
        <li>Format today's date in both locales, with a written-out month.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-javascript utilities-math">
    <h2>Math</h2>
    <div class="info">
      <p><b>Meteor shower</b></p>
      <ul>
        <li>Write a function that returns a random whole number of meteors between a minimum and a maximum, both included.</li>
        <li>Log the highest and the lowest count from an array of nightly observations, using spread.</li>
        <li>Log an average count rounded down, rounded up and rounded to the nearest whole number.</li>
        <li>A circular observation field has an area of 5000 square metres: log its radius and its circumference.</li>
        <li>Split a total meteor count into full groups of twelve plus a leftover, and log whether that total is even.</li>
        <li>Write a total of 1500000 observations in a readable way in your source code without changing its value, and log a count that is larger than the safe integer limit.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-javascript utilities-timers">
    <h2>Timers</h2>
    <div class="info">
      <p><b>Geyser</b></p>
      <ul>
        <li>Log "Eruption!" three seconds from now.</li>
        <li>Log a rumble every second, and stop after five rumbles.</li>
        <li>Start a delayed eruption and cancel it before it fires.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-javascript utilities-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Sunrise countdown</b></p>
      <p>Build a countdown to the next sunrise at a field station.</p>
      <ul>
        <li>Build the date of the next sunrise, which is tomorrow morning when today's has already passed, and calculate how many minutes and seconds are left from now.</li>
        <li>Log the remaining time every second with a repeating timer, padded to two digits for both minutes and seconds.</li>
        <li>Stop the timer at zero, clear it properly, and log the sunrise moment formatted for a Belgian reader, once as a date and once as a time.</li>
        <li>Format the station temperature and the distance to the reserve as localised numbers, one with a unit and one rounded.</li>
        <li>Use the maths helpers to pick a random observation slot between two hours, and to round a measurement up, down and to a fixed number of decimals.</li>
        <li>Parse a typed value like "12.5 km" into a number, and prove you handled the case where it cannot be parsed at all.</li>
        <li>Challenge: schedule one delayed log that fires at the halfway point of the countdown, and cancel it when the countdown is stopped early.</li>
      </ul>
    </div>
  </div>
</div>

<!-- OBJECT ORIENTED PROGRAMMING -->

<div data-id="exercises-object oriented programming" class="level1-container">
  <h1>Object oriented programming</h1>

  <div data-id="ex-object oriented programming-constructor functions">
    <h2>Constructor functions</h2>
    <div class="info">
      <p><b>Tree</b></p>
      <ul>
        <li>Write a constructor function for a tree with a species and a planting year.</li>
        <li>Create two trees with it and log their properties.</li>
        <li>Show what goes wrong when you call the constructor without the new keyword.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-object oriented programming-prototypes">
    <h2>Prototypes</h2>
    <div class="info">
      <p><b>Mammals</b></p>
      <ul>
        <li>Add an age method to your tree constructor's prototype and call it on both instances.</li>
        <li>Write a Mammal constructor and a Dolphin constructor that inherits from it through the prototype chain.</li>
        <li>Prove the inheritance with instanceof and by calling an inherited method.</li>
        <li>Prove the same link a second way, this time starting from the prototype object instead of from the instance.</li>
        <li>Log which properties a dolphin owns itself and which ones it only inherits.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-object oriented programming-classes">
    <h2>Classes</h2>
    <div class="info">
      <p><b>Birds</b></p>
      <ul>
        <li>Write a Bird class with a constructor, a fly method and a getter for its full description.</li>
        <li>Write a Penguin class that extends it and overrides the fly method, calling the parent constructor.</li>
        <li>Add a static method that creates a Bird from a comma-separated string.</li>
        <li>Create one more bird without using new and without a class, by linking it directly to an existing object as its prototype.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-object oriented programming-encapsulation">
    <h2>Encapsulation</h2>
    <div class="info">
      <p><b>Beehive</b></p>
      <ul>
        <li>Write a Beehive class that keeps its honey stock in a truly private field.</li>
        <li>Expose a harvest method that refuses to take more honey than is in stock.</li>
        <li>Show that reading the private field from outside the class throws, and make two methods chainable.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-object oriented programming-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Reserve registry</b></p>
      <p>A nature reserve has an old registry, written years ago with constructor functions. You are asked to keep it running and then migrate it, without losing what it already does.</p>
      <ul>
        <li>Write the old registry first: an Animal constructor function with a shared describe method on its prototype, and two animals created with it.</li>
        <li>Prove with a log that both animals share that one method through the prototype chain instead of each holding their own copy, and note in a comment what that saves.</li>
        <li>Migrate the same registry to a class, keeping describe working exactly as before, and add a Bird and a Mammal subclass that each extend describe by calling the parent version inside their own.</li>
        <li>The old registry kept its list of animals in a plain property that anyone could overwrite. In the class version, make that list private and expose it only through a getter that hands out a copy, then show that the old way of breaking it no longer works.</li>
        <li>The reserve wants to know how many animals were ever registered, also after some were removed. Add a static counter and a static method that reports it.</li>
        <li>Animals are stored with a birth year, but every report shows an age. Expose that with a getter, and let a setter accept an age and store the matching birth year.</li>
        <li>Challenge: log a report per species, and explain in a comment what a subclass does and does not inherit of the private field and the static counter.</li>
      </ul>
    </div>
  </div>
</div>

<!-- ERROR HANDLING -->

<div data-id="exercises-error handling" class="level1-container">
  <h1>Error handling</h1>

  <div data-id="ex-error handling-try catch finally">
    <h2>Try / catch / finally</h2>
    <div class="info">
      <p><b>Weather station log</b></p>
      <p>A weather station sends readings as JSON strings, but some arrive corrupted.</p>
      <ul>
        <li>Write a function that parses a reading string and logs the temperature it contains.</li>
        <li>Wrap the parsing in a try/catch so a corrupted reading logs a friendly message instead of crashing.</li>
        <li>Add a finally block that logs "Reading processed" regardless of whether parsing succeeded.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-error handling-throw and the error object">
    <h2>Throw &amp; the Error object</h2>
    <div class="info">
      <p><b>Fish tank feeding</b></p>
      <ul>
        <li>Write a function that feeds a fish tank, throwing an Error when the requested amount of food exceeds the stock.</li>
        <li>Call it once with a valid amount and once with too much, catching the second call.</li>
        <li>Log the error's message and name from inside the catch block.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-error handling-custom error classes">
    <h2>Custom error classes</h2>
    <div class="info">
      <p><b>Migration tracker</b></p>
      <ul>
        <li>Write a custom error class for an altitude that falls outside a bird's expected migration range.</li>
        <li>Write a function that validates a recorded altitude and throws that custom error when it is out of range.</li>
        <li>Catch it, use instanceof to confirm the error type, and log a message that is specific to this error. Re-throw anything else.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-error handling-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Aquarium controller</b></p>
      <p>A controller reads sensor values that arrive as strings and may be unparseable, out of range or missing entirely.</p>
      <ul>
        <li>Write at least two custom error classes that extend Error, each with its own name and its own extra information.</li>
        <li>Write a function that reads a single sensor value and throws the right custom error when it cannot be parsed or falls outside a safe range.</li>
        <li>Process an array of readings, catching errors per reading so one bad value does not stop the rest.</li>
        <li>Tell your error types apart in the catch block, and handle each one differently.</li>
        <li>Use finally to count every processed reading, and prove that it also runs when an error was thrown.</li>
        <li>Rethrow one specific error so it bubbles up to an outer try catch, and handle it there.</li>
        <li>Challenge: log a summary of how many readings succeeded versus failed, together with the name, message and stack of the first error you caught.</li>
      </ul>
    </div>
  </div>
</div>

<!-- ASYNCHRONOUS JAVASCRIPT -->

<div data-id="exercises-asynchronous javascript" class="level1-container">
  <h1>Asynchronous javascript</h1>

  <div data-id="ex-asynchronous javascript-asynchronicity">
    <h2>Asynchronicity</h2>
    <div class="info">
      <p><b>Seed germination</b></p>
      <ul>
        <li>Write a function that takes a seed name, a delay and a callback, and calls the callback after that delay.</li>
        <li>Use it to germinate a seed, then sprout it, then flower it, each step depending on the previous one.</li>
        <li>Log a line before and after the whole chain and explain the order you see.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-asynchronous javascript-promises">
    <h2>Promises</h2>
    <div class="info">
      <p><b>Spore dispersal</b></p>
      <ul>
        <li>Write a function that returns a promise which resolves with a landing spot, or rejects when the wind is too strong.</li>
        <li>Handle both outcomes with then and catch, and always log a closing line.</li>
        <li>Chain a second step that only runs after a successful landing.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-asynchronous javascript-ajax">
    <h2>AJAX</h2>
    <div class="info">
      <p><b>Weather station</b></p>
      <p>Use the open weather endpoint below, which needs no key.</p>
      <pre><code class="language-javascript">
        https://api.open-meteo.com/v1/forecast?latitude=51.05&amp;longitude=3.72&amp;current=temperature_2m
      </code></pre>
      <ul>
        <li>Fetch it with a then chain and log the current temperature.</li>
        <li>Log a line before and after the fetch call to show when each one runs.</li>
        <li>Do the same call a second time for a different location, without duplicating the fetch code.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-asynchronous javascript-async/await">
    <h2>Async/await</h2>
    <div class="info">
      <p><b>Rewritten station</b></p>
      <ul>
        <li>Rewrite the weather station exercise with async and await.</li>
        <li>Return the temperature from the async function and log what the function itself returns.</li>
        <li>Consume that returned value in two ways: with then, and with await in another async function.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-asynchronous javascript-error handling">
    <h2>Error handling</h2>
    <div class="info">
      <p><b>Unknown coordinates</b></p>
      <ul>
        <li>Call the weather endpoint with nonsense coordinates and handle the failure with try and catch.</li>
        <li>Throw your own error when the response status is not ok, and log its message.</li>
        <li>Show that a network failure and a 400 response need different checks.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-asynchronous javascript-promisifying">
    <h2>Promisifying</h2>
    <div class="info">
      <p><b>Wait helper</b></p>
      <ul>
        <li>Write a wait function that takes seconds and returns a promise that resolves after that time.</li>
        <li>Use it to log three growth stages of a fern, one second apart, without nesting.</li>
        <li>Promisify the callback-based germination function you wrote earlier.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-asynchronous javascript-promise combinators">
    <h2>Promise combinators</h2>
    <div class="info">
      <p><b>Three reserves</b></p>
      <ul>
        <li>Fetch the weather for three reserves at once and log all three temperatures together.</li>
        <li>Do the same when one of the three fails, and still report the two that worked.</li>
        <li>Log only the fastest of the three, and separately the first one that succeeds.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-asynchronous javascript-top level await">
    <h2>Top level await</h2>
    <div class="info">
      <p><b>Module-level fetch</b></p>
      <ul>
        <li>In a module, await the weather fetch at the top level and export the result.</li>
        <li>Import it from another module and log it.</li>
        <li>Add a log at the end of the importing module and explain the order you see.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-asynchronous javascript-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Field station dashboard</b></p>
      <p>Use this markup as your starting page.</p>
      <pre><code class="language-html">
        &lt;body&gt;
          &lt;h1&gt;Field station&lt;/h1&gt;
          &lt;p id="status"&gt;Loading ...&lt;/p&gt;
          &lt;ul id="readings"&gt;&lt;/ul&gt;
          &lt;script type="module" src="script.js"&gt;&lt;/script&gt;
        &lt;/body&gt;
      </code></pre>
      <ul>
        <li>Write the function that fetches one reserve with a plain promise chain, so you see the then and catch steps, and build everything that calls it with async await.</li>
        <li>Fetch the weather for three reserves in parallel and list each temperature as a list item.</li>
        <li>Handle failures per reserve so one broken request does not empty the whole list, and show an error message in the status paragraph.</li>
        <li>Use a combinator that waits for every result, including the failed ones, and log which reserves succeeded and which did not.</li>
        <li>Give the whole thing a three second limit by racing it against a timer, after which it gives up and says so.</li>
        <li>Promisify a callback based helper, for example a delay built on setTimeout, and use it to retry a failed reserve once.</li>
        <li>Use a top level await somewhere in the module, and explain in a comment why that is allowed here.</li>
        <li>Challenge: log the exact order in which your synchronous code, your promise callbacks and your timers run, and explain why.</li>
      </ul>
    </div>
  </div>
</div>

<!-- MODULES -->

<div data-id="exercises-modules" class="level1-container">
  <h1>Modules</h1>

  <div data-id="ex-modules-es6 modules">
    <h2>ES6 modules</h2>
    <div class="info">
      <p><b>Split the forest</b></p>
      <ul>
        <li>Move a tree-counting function into its own module and import it into your main script.</li>
        <li>Make the page load the main script as a module.</li>
        <li>Add a log line in the imported module and explain when it runs.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-modules-named and default exports">
    <h2>Named and default exports</h2>
    <div class="info">
      <p><b>Toolkit exports</b></p>
      <ul>
        <li>Give your module two named exports and one default export.</li>
        <li>Import the default and one named export in a single statement, renaming the named one.</li>
        <li>Import everything as one namespace object and call a function through it.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-modules-commonjs modules">
    <h2>CommonJS modules</h2>
    <div class="info">
      <p><b>Node version</b></p>
      <ul>
        <li>Rewrite the same module in CommonJS style and run it with Node.</li>
        <li>Export two functions at once and require them with destructuring.</li>
        <li>Log what happens if you try to use that file in the browser as a module.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-modules-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Weather toolkit</b></p>
      <ul>
        <li>Build a three file project: one module that fetches data, one that formats it, and one main script that ties them together.</li>
        <li>Use a default export in one module and named exports in the other, and import both in the main script.</li>
        <li>Rename one import on the way in, and import everything from one module as a single namespace object.</li>
        <li>Keep a counter inside the fetch module that is shared between all importers, and prove with two importing modules that they see the same value.</li>
        <li>Log something at the top of the main script and at the top of an imported module, and explain the order you get.</li>
        <li>Rewrite the formatting module in CommonJS style in a separate folder and run that version with node, then note in a comment why you cannot simply drop it into the browser project next to the ES6 modules.</li>
        <li>Challenge: import the same module twice and prove that its top level code only runs once.</li>
      </ul>
    </div>
  </div>
</div>

<!-- THIS KEYWORD -->

<div data-id="exercises-this keyword" class="level1-container">
  <h1>This keyword</h1>

  <div data-id="ex-this keyword-overview">
    <h2>Overview</h2>
    <div class="info">
      <p><b>Heron</b></p>
      <ul>
        <li>Build a heron object with a method that logs its own name through this.</li>
        <li>Add an arrow function as a method and log what this points at there.</li>
        <li>Add a regular function nested inside a method and log what this points at inside it, then fix it with an arrow.</li>
        <li>Attach a regular function as a click handler on a button and log what this points at, then do the same with an arrow function and compare the two.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-this keyword-method borrowing">
    <h2>Method borrowing</h2>
    <div class="info">
      <p><b>Borrowed call</b></p>
      <ul>
        <li>Write two bird objects where only one has a call method, and borrow that method for the other.</li>
        <li>Do the same while passing arguments, first one by one and then as an array.</li>
        <li>Borrow an array method to work on an array-like object of sightings.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-this keyword-function binding">
    <h2>Function binding</h2>
    <div class="info">
      <p><b>Bound ranger</b></p>
      <ul>
        <li>Pass an object's method to setTimeout and show that this breaks.</li>
        <li>Fix it with bind, and then a second time with an arrow function.</li>
        <li>Use bind to preset the first argument of a measuring function and call it with only the second.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-this keyword-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Observation counter</b></p>
      <ul>
        <li>Build an object that counts sightings and logs a running total from a regular method.</li>
        <li>Add a second method written as an arrow function, log this inside both, and explain the difference in a comment.</li>
        <li>Pull the regular method out into a standalone variable, call it, and show exactly how this gets lost.</li>
        <li>Fix that broken call three times: once by binding, once by borrowing with call or apply, and once with an arrow function wrapper.</li>
        <li>Register the counting method as a callback for a repeating timer so it keeps working, and stop the timer after five ticks.</li>
        <li>Borrow the same counting method for a second, unrelated object with the same shape.</li>
        <li>Challenge: bind one argument up front so you end up with a counter that always adds the same amount.</li>
      </ul>
    </div>
  </div>
</div>

<!-- BEHIND THE SCENES -->

<div data-id="exercises-behind the scenes" class="level1-container">
  <h1>Behind the scenes</h1>

  <div data-id="ex-behind the scenes-the callstack">
    <h2>The callstack</h2>
    <div class="info">
      <p><b>Nested streams</b></p>
      <ul>
        <li>Write three functions that call each other and log both on entering and on leaving.</li>
        <li>Predict the output order before running it, then check.</li>
        <li>Write a recursive function that measures a stream depth and make it hit the stack limit on purpose.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-behind the scenes-the callback queue">
    <h2>The callback queue</h2>
    <div class="info">
      <p><b>Delayed thaw</b></p>
      <ul>
        <li>Write a script with a timer of zero milliseconds between two normal logs, and predict the order.</li>
        <li>Add a slow loop that runs for a second after the timer is set, and explain the delay you measure.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-behind the scenes-the microtask queue">
    <h2>The microtask queue</h2>
    <div class="info">
      <p><b>Pollen burst</b></p>
      <ul>
        <li>Write a script that fires a resolved promise and a zero-millisecond timer, and predict which logs first.</li>
        <li>Add a second promise inside the first handler and predict again.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-behind the scenes-the event loop">
    <h2>The event loop</h2>
    <div class="info">
      <p><b>Full cycle</b></p>
      <ul>
        <li>Write one script that produces exactly this order: start, end, promise, timer.</li>
        <li>Change only the order of the lines you wrote, and produce: start, end, promise, promise, timer.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-behind the scenes-hoisting">
    <h2>Hoisting</h2>
    <div class="info">
      <p><b>Early spring</b></p>
      <ul>
        <li>Log a var variable before its declaration and a let variable before its declaration, and compare the two results.</li>
        <li>Call a function declaration and a function expression before both are defined, and compare again.</li>
        <li>Write a small script that proves the temporal dead zone exists.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-behind the scenes-scoping">
    <h2>Scoping</h2>
    <div class="info">
      <p><b>Nested canopy</b></p>
      <ul>
        <li>Write nested functions where an inner one reads a variable from two levels up.</li>
        <li>Shadow that variable in the inner scope and log both values.</li>
        <li>Create a loop that logs 0, 1, 2 with a timer, and a version that logs 3, 3, 3, by changing only the declaration keyword.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-behind the scenes-chapter challenge">
    <h2>Chapter challenge</h2>
    <div class="info">
      <p><b>Predict and prove</b></p>
      <ul>
        <li>Write one script that mixes synchronous logs, a zero millisecond timer, a resolved promise, a hoisted function call and a variable read before its declaration.</li>
        <li>Write down the output order you expect before running it, plus which line will throw and which will log undefined.</li>
        <li>Run it, then explain every line where your prediction was wrong, in terms of the callstack, the callback queue and the microtask queue.</li>
        <li>Prove that the microtask queue is emptied before the next timer callback runs, using at least two promises and two timers.</li>
        <li>Show the difference between a function declaration and a function expression when both are called before their definition.</li>
        <li>Show the difference between var, let and const in the same block, including the temporal dead zone.</li>
        <li>Challenge: build an example where a long running synchronous loop blocks a timer that was scheduled earlier, and measure the delay it causes.</li>
      </ul>
    </div>
  </div>
</div>

<!-- EXTRA -->

<div data-id="exercises-extra" class="level1-container">
  <h1>Extra</h1>

  <div data-id="ex-extra-glossary">
    <h2>Glossary</h2>
    <div class="info">
      <p><b>Open questions</b></p>
      <ol>
        <li>Explain the difference between a parameter and an argument.</li>
        <li>What does it mean that a function is a first class citizen in JavaScript?</li>
        <li>In your own words: what is the difference between a shallow and a deep copy?</li>
        <li>What is the difference between a statement and an expression, and where does that difference bite you in practice?</li>
        <li>What does idempotent mean, and give a nature-flavoured example of an idempotent operation.</li>
      </ol>
    </div>
    <div class="info">
      <p><b>Multiple choice</b></p>
      <p>1. A pure function is a function that ...</p>
      <ol type="a">
        <li>never returns a value</li>
        <li>always returns the same output for the same input and has no side effects</li>
        <li>only uses const</li>
        <li>can only be called once</li>
      </ol>
      <p>2. Mutation means ...</p>
      <ol type="a">
        <li>changing an existing value in place</li>
        <li>creating a new value from an old one</li>
        <li>converting a string to a number</li>
        <li>copying an object</li>
      </ol>
      <p>3. Which of these is not a primitive type?</p>
      <ol type="a">
        <li>string</li>
        <li>boolean</li>
        <li>array</li>
        <li>symbol</li>
      </ol>
      <p>4. The DOM is ...</p>
      <ol type="a">
        <li>part of the JavaScript language itself</li>
        <li>an interface the browser exposes to JavaScript</li>
        <li>a Node.js module</li>
        <li>a data structure like a Map</li>
      </ol>
    </div>
  </div>

  <div data-id="ex-extra-best practices">
    <h2>Best practices</h2>
    <div class="info">
      <p><b>Open questions</b></p>
      <ol>
        <li>Why is const the default choice over let, and when is let genuinely the right call?</li>
        <li>Name two reasons to avoid var entirely.</li>
        <li>Why should a function name usually contain a verb?</li>
        <li>When would you choose a for of loop over a chain of array methods?</li>
        <li>Why is it a bad idea to catch an error and do nothing with it?</li>
      </ol>
    </div>
    <div class="info">
      <p><b>Multiple choice</b></p>
      <p>1. Strict equality is preferred over loose equality because ...</p>
      <ol type="a">
        <li>it is faster to type</li>
        <li>it does not coerce types, so comparisons stay predictable</li>
        <li>it works on objects too</li>
        <li>loose equality is deprecated</li>
      </ol>
      <p>2. Which naming is the better choice for a function that returns whether a bird is migratory?</p>
      <ol type="a">
        <li>migratory</li>
        <li>checkBird</li>
        <li>isMigratory</li>
        <li>birdData</li>
      </ol>
      <p>3. A deeply nested chain of if statements is usually best replaced by ...</p>
      <ol type="a">
        <li>a single very long ternary</li>
        <li>early returns or guard clauses</li>
        <li>a try catch block</li>
        <li>more comments</li>
      </ol>
      <p>4. Which statement about comments is closest to good practice?</p>
      <ol type="a">
        <li>every line deserves a comment</li>
        <li>comments should explain what the code does</li>
        <li>comments should explain why the code does something non-obvious</li>
        <li>comments are never useful</li>
      </ol>
    </div>
  </div>
</div>

<!-- FINAL CHALLENGES -->

<div data-id="exercises-final challenges" class="level1-container">
  <h1>Final challenges</h1>

  <div data-id="ex-final challenges-species tracker">
    <h2>Species tracker</h2>
    <div class="info">
      <p><b>Nature reserve tracker</b></p>
      <ul>
        <li>Model reserves and sightings with classes, using private fields and a static counter.</li>
        <li>Keep unique species in a set and counts per species in a map, both derived from an array of raw sightings.</li>
        <li>Expose a report method that returns the top species sorted by count, formatted with a template literal.</li>
        <li>Split the whole thing over at least two modules.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-final challenges-live dashboard">
    <h2>Live dashboard</h2>
    <div class="info">
      <p><b>Reserve dashboard</b></p>
      <p>Start from this markup.</p>
      <pre><code class="language-html">
        &lt;body&gt;
          &lt;h1&gt;Reserve dashboard&lt;/h1&gt;
          &lt;p id="status"&gt;&lt;/p&gt;
          &lt;ul id="reserves"&gt;&lt;/ul&gt;
          &lt;script type="module" src="script.js"&gt;&lt;/script&gt;
        &lt;/body&gt;
      </code></pre>
      <ul>
        <li>Keep a list of three reserves with coordinates, and fetch their current temperature in parallel.</li>
        <li>Render one list item per reserve with the temperature formatted for a Belgian reader, sorted from warm to cold.</li>
        <li>Handle failures per reserve, so one broken request does not empty the whole list.</li>
        <li>Refresh the data every thirty seconds and stop refreshing after five rounds.</li>
      </ul>
    </div>
  </div>

  <div data-id="ex-final challenges-field notebook">
    <h2>Field notebook</h2>
    <div class="info">
      <p><b>Notebook parser</b></p>
      <ul>
        <li>Start from an array of raw note strings like "2024-05-03 | fern valley | red kite | 2".</li>
        <li>Parse each note into an object, skipping malformed lines without crashing.</li>
        <li>Group the results per location, then per species, and log a readable summary per location.</li>
        <li>Make the parsing function pure, and prove the input array is never mutated.</li>
      </ul>
    </div>
  </div>
</div>
`;
